import { SparklesIcon, XIcon } from 'lucide-react'
import type { ReactElement, ReactNode } from 'react'
import {
  IconButton,
  DetailsSectionSubtitle,
  DetailsSectionTitle,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverContent,
  Slot,
} from '../../../../core'
import { useDismissibleUserEducation } from '../hooks/useDismissibleUserEducation'
import { useIsVisibleUserEducation } from '../hooks/useIsVisibleUserEducation'
import type { UserEducationId } from '../userEducationPriority'
import { cn } from '@joyful/utils'
export interface UserEducationPopoverProps {
  children: ReactElement
  description: ReactNode
  educationId: UserEducationId
  footer?: ReactNode
  icon?: ReactElement
  isActive: boolean
  targetEmphasis?: 'caret' | 'ring'
  title: ReactNode
}

export function UserEducationPopover({
  children,
  description,
  educationId,
  footer,
  icon,
  isActive,
  targetEmphasis = 'ring',
  title,
}: UserEducationPopoverProps) {
  const education = useDismissibleUserEducation({ educationId })
  const isEligible = isActive && education.state.dismissedAt === null
  const isVisible = useIsVisibleUserEducation({
    educationId,
    isEligible,
  })
  const isOpen = isEligible && isVisible

  const handleOpenChange = (open: boolean) => {
    if (open === false) {
      education.actions.dismiss()
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <span
          className={cn(
            'inline-flex min-w-0 rounded-md transition-shadow',
            isOpen &&
              targetEmphasis === 'ring' &&
              'ring-offset-tremor-background ring-2 ring-blue-500/70 ring-offset-2',
          )}
        >
          {children}
        </span>
      </PopoverAnchor>
      <PopoverContent
        align="center"
        side="bottom"
        sideOffset={8}
        size="sm"
        variant="plain"
        onClick={(event) => {
          event.stopPropagation()
        }}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
        }}
        onFocusOutside={(event) => {
          event.preventDefault()
        }}
        onPointerDown={(event) => {
          event.stopPropagation()
        }}
      >
        {targetEmphasis === 'caret' ? (
          <PopoverArrow asChild>
            <svg aria-hidden="true" height="10" viewBox="0 0 22 10" width="22">
              <polygon
                className="fill-blue-50 dark:fill-blue-950"
                points="0,0 11,10 22,0"
              />
              <path
                className="fill-none stroke-blue-500/30 stroke-1 dark:stroke-blue-400/30"
                d="M0 0 L11 10 L22 0"
              />
            </svg>
          </PopoverArrow>
        ) : null}
        <div className="relative flex flex-col items-start gap-y-2 rounded-md border border-blue-500/30 bg-blue-50 py-2 pr-8 pb-4 pl-4 text-blue-800 shadow-xl dark:bg-blue-950 dark:text-blue-400">
          <div className="absolute top-2 right-2">
            <IconButton
              variant="ghost"
              size="xs"
              tooltip={`Dismiss`}
              onClick={() => {
                education.actions.dismiss()
              }}
            >
              <XIcon className="size-3.5" />
            </IconButton>
          </div>

          <div className="flex min-w-0 flex-col gap-y-1 pt-2">
            <div className="flex min-w-0 items-center gap-x-2">
              <Slot className="size-4.5 shrink-0 text-blue-600 dark:text-blue-400">
                {icon ?? <SparklesIcon />}
              </Slot>

              <div className="text-blue-800 dark:text-blue-400">
                <DetailsSectionTitle size="sm" tone="inherit">
                  {title}
                </DetailsSectionTitle>
              </div>
            </div>

            <div className="text-blue-800/90 dark:text-blue-400/90">
              <DetailsSectionSubtitle size="xs" tone="inherit">
                {description}
              </DetailsSectionSubtitle>
            </div>
          </div>

          {footer ? <div className="pt-1">{footer}</div> : null}
        </div>
      </PopoverContent>
    </Popover>
  )
}
